import QRCode from 'qrcode';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * QR Code Generation Service
 * Handles QR code generation and Firebase Storage upload
 * This version works with the frontend Firebase client SDK
 */
export class QRCodeService {
  constructor(firestore) {
    this.firestore = firestore;
    this.storage = getStorage();
  }

  /**
   * Generate QR code for an asset
   * @param {string} assetId - The asset ID
   * @returns {Promise<string>} - Data URL of the generated QR code
   */
  async generateQRCode(assetId) {
    try {
      const assetUrl = `https://dashboard.pristinemaint.com/assets/${assetId}`;
      
      const qrCodeOptions = {
        errorCorrectionLevel: 'M',
        type: 'image/png',
        quality: 0.92,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        width: 256
      };

      const qrCodeDataUrl = await QRCode.toDataURL(assetUrl, qrCodeOptions);
      return qrCodeDataUrl;
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw new Error(`Failed to generate QR code: ${error.message}`);
    }
  }

  /**
   * Upload QR code to Firebase Storage
   * @param {string} assetId - The asset ID
   * @param {string} qrCodeDataUrl - Data URL of the QR code
   * @returns {Promise<string>} - Public URL of the uploaded QR code
   */
  async uploadQRCode(assetId, qrCodeDataUrl) {
    try {
      // Convert data URL to blob
      const response = await fetch(qrCodeDataUrl);
      const blob = await response.blob();

      // Define file path in Firebase Storage
      const fileName = `${assetId}.png`;
      const filePath = `QR_codes/${fileName}`;
      
      // Create a reference to the file
      const qrCodeRef = ref(this.storage, filePath);

      // Upload the blob to Firebase Storage
      const uploadResult = await uploadBytes(qrCodeRef, blob, {
        contentType: 'image/png',
        cacheControl: 'public, max-age=31536000', // Cache for 1 year
      });

      // Get the download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      console.log(`QR code uploaded successfully for asset ${assetId}: ${downloadURL}`);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading QR code to Firebase Storage:', error);
      throw new Error(`Failed to upload QR code: ${error.message}`);
    }
  }

  /**
   * Generate and upload QR code for an asset (complete workflow)
   * @param {string} assetId - The asset ID
   * @returns {Promise<string>} - Public URL of the uploaded QR code
   */
  async generateAndUploadQRCode(assetId) {
    try {
      console.log(`Generating QR code for asset: ${assetId}`);
      
      // Generate QR code
      const qrCodeDataUrl = await this.generateQRCode(assetId);
      
      // Upload to Firebase Storage
      const publicUrl = await this.uploadQRCode(assetId, qrCodeDataUrl);
      
      return publicUrl;
    } catch (error) {
      console.error(`Error in generateAndUploadQRCode for asset ${assetId}:`, error);
      throw error;
    }
  }

  /**
   * Delete QR code from Firebase Storage
   * @param {string} assetId - The asset ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteQRCode(assetId) {
    try {
      const fileName = `${assetId}.png`;
      const filePath = `QR_codes/${fileName}`;
      
      // Create a reference to the file
      const qrCodeRef = ref(this.storage, filePath);

      // Note: Firebase client SDK doesn't have a direct delete method
      // We'll need to use a Cloud Function or Admin SDK for deletion
      // For now, we'll just log that deletion was attempted
      console.log(`QR code deletion requested for asset ${assetId} (requires Cloud Function implementation)`);
      
      // TODO: Implement Cloud Function for QR code deletion
      return true;
    } catch (error) {
      console.error(`Error deleting QR code for asset ${assetId}:`, error);
      throw new Error(`Failed to delete QR code: ${error.message}`);
    }
  }
}
